import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { RiFileExcel2Line } from 'react-icons/ri';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { MdExposureNeg1 } from 'react-icons/md';
import { TbDecimal } from 'react-icons/tb';
import { useQueryClient } from '@tanstack/react-query';
import FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';
import { selectedMembership, useAxios } from '@intly/core';
import Banner from '../../../ui/Banner/Banner';
import {
  useCostCenterReview,
  useCostCenterReviewCompanies,
} from './useCostCenterReview';
import UnmappedValuesIndicator from './UnmappedValuesIndicator';
import CostCenterReviewDetails from './CostCenterReviewDetails';
import CostCenterReviewKpi from './CostCenterReviewKpi';
import ReviewTransactionsDialog from './ReviewTransactionsDialog';
import ReviewFteDialog from './ReviewFteDialog';
import ReviewEmployeesDialog from './ReviewEmployeesDialog';
import ReviewFetchedDataDialog from './ReviewFetchedDataDialog';
import {
  COST_CENTER_REVIEW,
  includeOrdinaryOf,
  ReviewCell,
  sourceTableOf,
} from './costCenterReview.types';
import type { ReportCacheEntry } from '../../Chart/ReportChart/CacheEntryList';
import { buildReviewWorkbook } from './reviewExport';
import { MinusLeftContext, ShowOreContext } from './reviewTableStyles';
import type { PrebuiltDashboardProps } from '../registry';

type ReviewTab = 'kpi' | 'details';

// A tiny view toggle: an icon for a label and its meaning in the tooltip, so it doesn't
// compete with the toolbar's selects.
const toggleSwitchSx = {
  width: 26,
  height: 16,
  p: '4px',
  '& .MuiSwitch-switchBase': {
    p: '3px',
    '&.Mui-checked': { transform: 'translateX(10px)' },
  },
  '& .MuiSwitch-thumb': { width: 10, height: 10 },
};

const ViewToggle: FC<{
  icon: ReactNode;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ icon, hint, checked, onChange }) => (
  <Tooltip title={hint}>
    <FormControlLabel
      sx={{ m: 0, alignSelf: 'center', gap: 0.25, color: 'text.secondary' }}
      control={
        <Switch
          size="small"
          sx={toggleSwitchSx}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': hint }}
        />
      }
      label={<Box sx={{ display: 'flex', fontSize: 16 }}>{icon}</Box>}
    />
  </Tooltip>
);

const CostCenterReview: FC<PrebuiltDashboardProps> = ({ settings }) => {
  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { backendDataRequest } = useAxios();
  const groupId = useReactiveVar(selectedMembership);
  const [exporting, setExporting] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [costCenter, setCostCenter] = useState<string | null>(null);
  const [tab, setTab] = useState<ReviewTab>('details');
  const [expandCell, setExpandCell] = useState<ReviewCell | null>(null);
  // "Expandera per anställd"; picking an employee opens `expandCell` on top of it
  const [employeesCell, setEmployeesCell] = useState<ReviewCell | null>(null);
  const [ftePeriod, setFtePeriod] = useState<string | null>(null);
  const [fetchedDataOpen, setFetchedDataOpen] = useState(false);
  const [showOre, setShowOre] = useState(true);
  const [minusLeft, setMinusLeft] = useState(false);
  const queryClient = useQueryClient();

  const companies = useCostCenterReviewCompanies();
  // Loaded once for both tabs, so switching tabs doesn't refetch.
  const {
    meta,
    periods,
    periodStatus,
    rows,
    byAccountingType,
    accountAttributeLabel,
    byPayGroup,
    allLoaded,
  } = useCostCenterReview(companyId, costCenter, settings);

  useEffect(() => {
    if (!companyId && companies.data?.length) {
      setCompanyId(companies.data[0].id);
    }
  }, [companies.data, companyId]);

  const handleCompanyChange = (id: string) => {
    setCompanyId(id);
    setCostCenter(null);
  };

  const costCenters = meta.data?.costCenters ?? [];
  const company = companies.data?.find(({ id }) => id === companyId);
  const companyName =
    company?.name ?? company?.companyRegistrationNumber ?? companyId ?? '';
  const costCenterLabel =
    costCenters.find(({ value }) => value === costCenter)?.label ??
    costCenter ??
    (t('ALL') as string);

  // A re-fetched cache entry changes its period's figures (and, for the newest period, the
  // cost center list), so those queries load again; every other period stays as it is.
  const handleRefreshed = (entry: ReportCacheEntry) => {
    const period = (entry.paymentDate ?? '').replace(/\D/g, '').slice(0, 6);
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === 'prebuilt' &&
        queryKey[1] === COST_CENTER_REVIEW &&
        queryKey[4] === companyId &&
        ((['data', 'rows'].includes(queryKey[2] as string) &&
          queryKey[6] === period) ||
          (queryKey[2] === 'meta' && period === periods[0])),
    });
  };
  const noCompanies = companies.isSuccess && !companies.data.length;
  const noData =
    meta.isSuccess && allLoaded && periods.length > 0 && rows.length === 0;

  // The backend draws the workbook from the figures already on screen (both tabs).
  const handleExport = async () => {
    const workbook = buildReviewWorkbook({
      t: t as (key: string, options?: Record<string, unknown>) => string,
      locale: i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE',
      companyName,
      costCenterLabel,
      periodStatus,
      rows,
      byAccountingType,
      accountAttributeLabel,
      byPayGroup,
      primaryColor: palette.primary.main,
    });
    setExporting(true);
    try {
      const response = await backendDataRequest('data', {
        method: 'POST',
        data: {
          function: 'prebuilt_dashboard_export',
          groupId,
          dashboard: COST_CENTER_REVIEW,
          workbook,
        },
      });
      const url = response?.data?.result?.url;
      if (!url) {
        throw new Error(response?.data?.error ?? 'No file');
      }
      FileSaver.saveAs(url);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('EXPORT_TO_EXCEL_FAILED') as string, {
        variant: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <ShowOreContext.Provider value={showOre}>
      <Box sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label={t('COMPANY')}
            sx={{ minWidth: 240 }}
            value={companyId ?? ''}
            disabled={!companies.data?.length}
            onChange={(e) => handleCompanyChange(e.target.value)}
          >
            {(companies.data ?? []).map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name ??
                  company.companyRegistrationNumber ??
                  company.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('COST_CENTER')}
            sx={{ minWidth: 240 }}
            value={costCenter ?? ''}
            disabled={!costCenters.length}
            onChange={(e) => setCostCenter(e.target.value || null)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('ALL')}</MenuItem>
            {costCenters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UnmappedValuesIndicator periods={periodStatus} />
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <ViewToggle
              icon={<TbDecimal />}
              hint={t('SHOW_ORE')}
              checked={showOre}
              onChange={setShowOre}
            />
            {tab === 'details' && (
              <ViewToggle
                icon={<MdExposureNeg1 />}
                hint={t('MINUS_SIGN_LEFT')}
                checked={minusLeft}
                onChange={setMinusLeft}
              />
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'center' }}
            startIcon={<IoCloudDownloadOutline />}
            disabled={!companyId}
            onClick={() => setFetchedDataOpen(true)}
          >
            {t('REVIEW_FETCHED_DATA')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'center' }}
            startIcon={
              exporting ? <CircularProgress size={16} /> : <RiFileExcel2Line />
            }
            disabled={
              exporting || !allLoaded || !rows.length || Boolean(meta.isError)
            }
            onClick={handleExport}
          >
            {t('EXPORT_TO_EXCEL')}
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value: ReviewTab) => setTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="kpi" label={t('KPI')} />
          <Tab value="details" label={t('DETAILS')} />
        </Tabs>

        {noCompanies && <Banner severity="info" message={t('NO_COMPANIES')} />}
        {meta.isError && (
          <Banner severity="error" message={(meta.error as Error).message} />
        )}
        {noData && <Banner severity="info" message={t('NO_DATA')} />}

        {companyId && !meta.isError && tab === 'details' && (
          <MinusLeftContext.Provider value={minusLeft}>
            <CostCenterReviewDetails
              metaLoading={meta.isLoading}
              periods={periods}
              periodStatus={periodStatus}
              rows={rows}
              byAccountingType={byAccountingType}
              accountAttributeLabel={accountAttributeLabel}
              byPayGroup={byPayGroup}
              noData={noData}
              onExpand={setExpandCell}
              onExpandPerEmployee={setEmployeesCell}
              onExpandFte={setFtePeriod}
            />
          </MinusLeftContext.Provider>
        )}
        {companyId && !meta.isError && !noData && tab === 'kpi' && (
          <CostCenterReviewKpi
            periodStatus={periodStatus}
            rows={rows}
            byAccountingType={byAccountingType}
            accountAttributeLabel={accountAttributeLabel}
            byPayGroup={byPayGroup}
            allLoaded={allLoaded}
          />
        )}

        {companyId && (
          <ReviewEmployeesDialog
            cell={employeesCell}
            onClose={() => setEmployeesCell(null)}
            onSelectEmployee={(employee) =>
              employeesCell && setExpandCell({ ...employeesCell, employee })
            }
            companyId={companyId}
            companyName={companyName}
            costCenterColumn={meta.data?.costCenterColumn ?? null}
            costCenter={costCenter}
            costCenterLabel={costCenterLabel}
            settings={settings}
          />
        )}
        {companyId && (
          <ReviewTransactionsDialog
            cell={expandCell}
            onClose={() => setExpandCell(null)}
            companyId={companyId}
            companyName={companyName}
            costCenterColumn={meta.data?.costCenterColumn ?? null}
            costCenter={costCenter}
            costCenterLabel={costCenterLabel}
            settings={settings}
          />
        )}
        {companyId && (
          <ReviewFteDialog
            period={ftePeriod}
            onClose={() => setFtePeriod(null)}
            companyId={companyId}
            companyName={companyName}
            costCenterColumn={meta.data?.costCenterColumn ?? null}
            costCenter={costCenter}
            costCenterLabel={costCenterLabel}
            settings={settings}
          />
        )}
        {companyId && (
          <ReviewFetchedDataDialog
            open={fetchedDataOpen}
            onClose={() => setFetchedDataOpen(false)}
            companyId={companyId}
            companyName={companyName}
            sourceTable={sourceTableOf(settings)}
            includeOrdinary={includeOrdinaryOf(settings)}
            onRefreshed={handleRefreshed}
          />
        )}
      </Box>
    </ShowOreContext.Provider>
  );
};

export default CostCenterReview;
